import React, {
   useEffect,
   useState,
   MouseEvent,
   memo,
   useRef,
   useMemo,
   useCallback,
} from 'react'
import classnames from 'classnames'
import {
   AuditNoteDetails,
   CheckoutAction,
   GetItemOptions,
   ItemAuditedListProps,
   ViewName,
} from './itemAuditedList.type'
import * as styles from './itemAuditList.module.scss'
import { Item, Severity, ToolTypes } from '../../../types'
import {
   Alert,
   Button,
   CrudItems,
   IconButton,
   Image,
   LoadingOverlay,
   Modal,
   SnackBar,
   TableWrapper,
} from '..'
import { PATH } from '../../../constants/global'
import {
   useSettings,
   useItemUpload,
   useItemNotes,
   useItemHistory,
   useItemsView,
   useItems,
   useProfile,
   useItemGetKits,
   useAudit,
   useAPI,
   useConnections,
} from '../../../hooks'
import * as auditsStyles from '../../views/audits/audits.module.scss'
import { Col, Row } from 'react-grid-system'
import { AuditItem } from '../../../hooks/useAudit/useAudit.type'
import { AuditStatus } from '../../views/audits/allAudits/enums'
import { CrudItemsParentView } from '../../../constants/crudItems'
import { useMediaQuery } from 'react-responsive'
import { Actions } from '../../../constants/tables'
import { WarningModal } from '../warningModal/warningModal'
import { CRUD_TABLE } from '../../views/items/constants'
import {
   ManageKitModal,
   AddToKitModal,
   RemoveFromKitModal,
} from '../kitModals/'
import {
   convertConnectionsToOptions,
   convertNoteTypesToOptions,
} from '../filters/helpers'
import { SnackBarState } from '../snackBar/types'
import { closedSnackbarState } from '../snackBar/snackBar'
import { ACTION_CONSTANTS } from '../../views/actions/constants'
import { ItemButton } from '../modal/types'
import { View } from '../../actionsWorkflow/actionsWorkflow.type'
import AuditNoteModal from './auditNoteModal'
import { CheckoutModal } from '../../actionsWorkflow'
import { formatPrice } from '../../../utils/functions'
import { TabStatus } from '../crudItems/constants'
import ItemQuantityModal from '../../views/items/itemQuantityModal/itemQuantityModal'
import { InventoryWarehouse, ItemWarehouse } from '../crudItems/inventory/types'
import {
   convertInventoryWarehouseToOption,
   convertItemWarehouseToInventoryWarehouse,
   convertOptionToInventoryWarehouse,
} from '../crudItems/inventory/helpers'
import { Option } from '../form/fields/dropdown/types'
import AdjustModal from '../adjustModal/adjustModal'
import { renderDateOrNothing } from '../../../utils/datetime'
import QuickFilterButtons from '../quickFilterButtons/quickFilterButtons'

const ItemAuditedList = ({
   confirmed,
   unconfirmed,
   found,
   unconfirmedTotalCount,
   confirmedTotalCount,
   foundTotalCount,
   onRemoveFromAudit,
   selectedAuditFormData,
   itemTypeOptions,
   categoryOptions,
   manufacturerOptions,
   locationOptions,
   conditionOptions,
   auditIsLoading,
   handleAddAuditItem,
   setItemsWereAddedOrRemoved,
}: ItemAuditedListProps) => {
   const { settings } = useSettings()
   const [loading, setLoading] = useState<boolean>(false)
   const [selectedView, setSelectedView] = useState<ViewName>(
      ViewName.UNCONFIRMED
   )
   const [itemToRemove, setItemToRemove] = useState<Item | null>(null)
   const [pagedTableData, setPagedTableData] = useState<AuditItem[] | Item[]>(
      []
   )
   const [selectedItemUniqueId, setSelectedItemUniqueId] = useState<
      number | null
   >(null)
   const [selectedAuditItemId, setSelectedAuditItemId] = useState<
      number | null
   >(null)
   const [auditNoteModalOpen, setAuditNoteModalOpen] = useState<boolean>(false)
   const [auditNoteDetails, setAuditNoteDetails] =
      useState<AuditNoteDetails | null>(null)

   const {
      QuantityEnabled,
      KitEnabled,
      EnableLocationTracking,
      CustomText1Enabled,
      CustomText1Label,
      CustomText2Enabled,
      CustomText2Label,
   } = settings

   const { list: connections } = useConnections()

   const connectionOptions = convertConnectionsToOptions(connections)

   const { getUnconfirmedItems, getConfirmedItems, getFoundItems } = useAudit()

   useEffect(() => {
      if (!auditIsLoading) {
         switch (selectedView) {
            case ViewName.UNCONFIRMED:
               setPagedTableData(unconfirmed ?? [])
               break
            case ViewName.CONFIRMED:
               setPagedTableData(confirmed ?? [])
               break
            case ViewName.FOUND:
               setPagedTableData(found ?? [])
               break
         }
      }
   }, [selectedView, auditIsLoading, unconfirmed, confirmed, found])

   const handleTabClick = (
      viewName: ViewName,
   ) => {
      setSelectedView(Number(viewName))
   }

   const removeItemFromAudit = (item: Item) => onRemoveFromAudit(item)

   const prepareItemForRemoval = (rowItem: Item) => setItemToRemove(rowItem)

   const handleConfirmRemoveItem = () => {
      if (!itemToRemove) return
      handleSnackbar('Item has been removed from the audit.', Severity.SUCCESS)
      onRemoveFromAudit(itemToRemove) // existing saved audit send auditId to be deleted
      setItemToRemove(null)
      setItemsWereAddedOrRemoved(true)
      setIsItemModalVisible(false)
   }

   const handleCancelRemoveItem = () => {
      setItemToRemove(null)
      setIsItemModalVisible(false)
   }

   const handleGetItems = async (
      query?: string,
      skip?: number,
      take?: number,
      filters?: {}
   ) => {
      setLoading(true)
      if (
         !selectedAuditFormData.AuditID ||
         (selectedAuditFormData.AuditID &&
            selectedView === ViewName.UNCONFIRMED &&
            selectedAuditFormData.Status === AuditStatus.DRAFT)
      ) {
         const pagedData = unconfirmed.slice(skip, take + skip)
         setPagedTableData(pagedData)
      } else if (
         selectedAuditFormData.AuditID &&
         selectedView === ViewName.UNCONFIRMED &&
         selectedAuditFormData.Status !== AuditStatus.DRAFT
      ) {
         const items = await getUnconfirmedItems({
            auditID: Number(selectedAuditFormData.AuditID),
            skip: skip,
            take: take,
         })
         setPagedTableData(items)
      } else if (
         selectedAuditFormData.AuditID &&
         selectedView === ViewName.CONFIRMED
      ) {
         const items = await getConfirmedItems({
            auditID: Number(selectedAuditFormData.AuditID),
            skip: skip,
            take: take,
         })
         setPagedTableData(items)
      } else if (
         selectedAuditFormData.AuditID &&
         selectedView === ViewName.FOUND
      ) {
         const items = await getFoundItems({
            auditID: Number(selectedAuditFormData.AuditID),
            skip: skip,
            take: take,
         })
         setPagedTableData(items)
      }
      setLoading(false)
   }

   const handleViewNoteClick = (rowItem: Item) => {
      setAuditNoteDetails({
         ConfirmationStatus: rowItem?.Confirmation,
         Condition: rowItem?.ConditionDesc,
         AuditNote: rowItem?.AuditNote,
      })
      setAuditNoteModalOpen(true)
   }

   const handleAuditNoteModalClose = () => {
      setAuditNoteModalOpen(false)
      setAuditNoteDetails(null)
   }

   const columns = useMemo(
      () => [
         {
            Header: '',
            accessor: 'Id',
            minWidth: 50,
            maxWidth: 50,
            align: 'center',
            Cell: ({ row }) => {
               const rowItem = row?.original as Item
               return (
                  <>
                     {rowItem?.AuditNote && (
                        <IconButton
                           id="notes"
                           tooltip="View Note"
                           iconStroke="#000000"
                           action={() => handleViewNoteClick(rowItem)}
                        />
                     )}
                     {selectedAuditFormData?.Status === AuditStatus.DRAFT && (
                        <IconButton
                           id="delete"
                           tooltip="Remove Item"
                           action={() => prepareItemForRemoval(rowItem)}
                        />
                     )}
                  </>
               )
            },
         },
         {
            Header: 'Pic',
            accessor: 'PictureURL',
            id: 'PictureURL',
            allowBlank: true,
            maxWidth: 45,
            minWidth: 45,
            align: 'center',
            Cell: (cell: { row: { original: { PictureURL: string } } }) => (
               <Image
                  url={cell.row.original.PictureURL}
                  alt={cell.row.original.PictureURL}
                  defaultImagePadding={3}
                  width={25}
               />
            ),
         },
         {
            Header: 'Item Name',
            accessor: 'Title',
            id: 'Title',
            minWidth: 200,
         },
         ...(selectedView === ViewName.CONFIRMED
            ? [
                 {
                    Header: 'Confirmation',
                    accessor: 'Confirmation',
                    id: 'Confirmation',
                    minWidth: 100,
                 },
              ]
            : []),
         {
            Header: 'Condition',
            accessor: 'ConditionDesc',
            id: 'ConditionDesc',
            width: 100,
            minWidth: 100,
         },

         {
            Header: 'Audit Note',
            accessor: 'AuditNote',
            id: 'AuditNote',
            Cell: (cell: { row: { original: { AuditNote: string } } }) => {
               const { AuditNote } = cell.row.original
               return <div title={AuditNote ?? ''}>{AuditNote ?? '-'}</div>
            },
         },
         ...(selectedView === ViewName.FOUND
            ? [
                 {
                    id: 'DateAudited',
                    Header: 'Date Audited',
                    accessor: 'DateAudited',
                    width: 100,
                    minWidth: 20,
                    Cell: renderDateOrNothing,
                 },
              ]
            : []),
         {
            Header: 'Status',
            accessor: 'StatusDesc',
            id: 'StatusDesc',
            width: 80,
            minWidth: 80,
         },
         {
            Header: 'Loaned/Pending To',
            accessor: (row) => {
               return row.AssignedToUser ?? ''
            },
            id: 'AssignedToUser',
            minWidth: 100,
         },
         ...(EnableLocationTracking
            ? [
                 {
                    Header: 'Current/Pending Location',
                    accessor: 'CurrentLocation',
                    id: 'CurrentLocation',
                    minWidth: 200,
                 },
              ]
            : []),
         ...(KitEnabled
            ? [
                 {
                    Header: 'Assigned to Kit',
                    accessor: 'AssignedToKitName',
                    id: 'AssignedToKitName',
                    minWidth: 200,
                 },
              ]
            : []),
         ...(QuantityEnabled
            ? [
                 {
                    Header: 'Total Quantity',
                    id: 'Quantity',
                    accessor: 'Quantity',
                    width: 120,
                    minWidth: 50,
                 },
                 {
                    Header: 'Audit Quantity',
                    accessor: 'AuditQuantity',
                    id: 'AuditQuantity',
                    width: 120,
                    minWidth: 50,
                 },
                 {
                    Header: 'Status Quantity',
                    accessor: 'StatusQuantity',
                    id: 'StatusQuantity',
                    width: 120,
                    minWidth: 50,
                 },
              ]
            : []),
         {
            Header: 'Manufacturer',
            accessor: 'Manufacturer',
            id: 'Manufacturer',
            minWidth: 200,
         },
         {
            Header: 'Model No.',
            accessor: 'ModelNumber',
            id: 'ModelNumber',
            minWidth: 200,
         },
         {
            Header: 'Serial No.',
            accessor: 'SerialNumber',
            id: 'SerialNumber',
            minWidth: 200,
         },
         {
            Header: 'Barcode',
            accessor: (row) => row.Barcode ?? row.ItemBarcode,
            id: 'Barcode',
         },
         {
            Header: 'Category',
            accessor: 'Category',
            id: 'Category',
         },
         {
            Header: 'Type',
            accessor: 'ToolType',
            id: 'ToolType',
            minWidth: 100,
            Cell: ({ row }) => ToolTypes[row.original.ToolType] ?? 'Unknown',
         },
         ...(EnableLocationTracking
            ? [
                 {
                    Header: 'Home Warehouse',
                    accessor: 'DefaultLocation',
                    id: 'DefaultLocation',
                    minWidth: 200,
                 },
              ]
            : []),
         {
            Header: 'Purchase Price',
            accessor: (row) => formatPrice(row.PurchasePrice),
            id: 'PurchasePrice',
            minWidth: 200,
         },
         ...(CustomText1Enabled
            ? [
                 {
                    Header: CustomText1Label,
                    accessor: 'CustomText1Value',
                    id: 'CustomText1Value',
                    minWidth: 100,
                 },
              ]
            : []),
         ...(CustomText2Enabled
            ? [
                 {
                    Header: CustomText2Label,
                    accessor: 'CustomText2Value',
                    id: 'CustomText2Value',
                    minWidth: 100,
                 },
              ]
            : []),
         {
            Header: 'Item ID',
            accessor: 'ItemId',
            id: 'ItemId',
            minWidth: 100,
         },
      ],
      [
         selectedAuditFormData,
         selectedView,
         CustomText1Enabled,
         CustomText1Label,
         CustomText2Enabled,
         CustomText2Label,
      ]
   )

   const noItemsText = 'No items to show'

   // Items modal functionality
   const { profile } = useProfile()
   const [isItemModalVisible, setIsItemModalVisible] = useState<boolean>(false)
   const [activeValue, setActiveValue] = useState<any>({})
   const [itemForm, setItemForm] = useState<any>({})
   const isMobile = useMediaQuery({ query: '(max-width: 1000px)' })
   const [itemImages, setItemImages] = useState<any>([])
   const [tabStatus, setTabStatus] = useState<TabStatus>(TabStatus.DETAILS)
   const [itemId, setItemId] = useState<number | null>(null)
   const [itemAction, setItemAction] = useState<Actions>(Actions.Readonly)
   const [isCopyPrompt, setIsCopyPrompt] = useState<boolean>(false)
   const [isDeletePrompt, setIsDeletePrompt] = useState<boolean>(false)
   const [isNewItemNote, setIsNewItemNote] = useState<boolean>(false)
   const [isManageKitModalOpen, setIsManageKitModalOpen] =
      useState<boolean>(false)
   const [isAddToKitModalOpen, setIsAddToKitModalOpen] =
      useState<boolean>(false)
   const [isRemoveFromKitModal, setIsRemoveFromKitModal] =
      useState<boolean>(false)
   const [addToKitQty, setAddToKitQty] = useState<number>(1)
   const [itemKitOptions, setItemKitOptions] = useState([])
   const [isItemLimitPrompt, setIsItemLimitPrompt] = useState<boolean>(false)
   const [snackbar, setSnackbar] = useState<SnackBarState>(closedSnackbarState)
   const [selectedItemsForWorkflow, setSelectedItemsForWorkflow] = useState<
      Item[]
   >([])

   const checkoutOptions = useMemo(
      () => ACTION_CONSTANTS.companyCheckoutOptions,
      [ACTION_CONSTANTS]
   )
   const [checkoutAction, setCheckoutAction] = useState<CheckoutAction>(
      checkoutOptions[0]
   )
   const [isAdjustModalOpen, setIsAdjustModalOpen] = useState<boolean>(false)
   const [isCheckoutModalOpen, setIsCheckoutModalOpen] =
      useState<boolean>(false)
   const [isQuantityModalOpen, setIsQuantityModalOpen] =
      useState<boolean>(false)
   const [workflowItem, setWorkflowItem] = useState<any>(null)

   const [selectedWarehouseForCheckout, setSelectedWarehouseForCheckout] =
      useState<InventoryWarehouse | null>(null)
   const [selectedTransferToWarehouse, setSelectedTransferToWarehouse] =
      useState<InventoryWarehouse | null>(null)
   const [warehouseSelectionDisabled, setWarehouseSelectionDisabled] =
      useState<boolean>(null)

   const inventoryWarehouses: InventoryWarehouse[] = useMemo(
      () =>
         itemForm?.Warehouses?.map((wh) =>
            convertItemWarehouseToInventoryWarehouse(wh)
         ) ?? [],
      [itemForm]
   )

   const warehouseOptions = useMemo(
      () =>
         inventoryWarehouses
            ?.filter((wh) => wh?.toolWarehouseId)
            .map((wh) => convertInventoryWarehouseToOption(wh)) ?? [],
      [inventoryWarehouses]
   )

   const resetRef = useRef<HTMLInputElement | null>(null)

   const itemInventoryWarehouse = useMemo(
      () => convertItemWarehouseToInventoryWarehouse(itemForm.Warehouse),
      [itemForm?.Warehouse]
   )

   useEffect(() => {
      if (selectedWarehouseForCheckout) {
         setAddToKitQty(selectedWarehouseForCheckout.quantity)
      } else if (itemInventoryWarehouse) {
         setAddToKitQty(itemInventoryWarehouse.quantity)
      } else {
         setAddToKitQty(1)
      }
   }, [selectedWarehouseForCheckout, itemInventoryWarehouse])

   useEffect(() => {
      if (selectedAuditFormData.AuditID && selectedAuditFormData.Status !== AuditStatus.DRAFT) {
         setSelectedView(ViewName.CONFIRMED)
      }
   }, [selectedAuditFormData.Status])

   const allItemButtons: ItemButton[] = [
      {
         name: 'actionLoan',
         title: 'Loan',
         action: () => handleWorkflowAction('Loan', itemInventoryWarehouse),
         type: 'Loan',
      },
      {
         name: 'actionTransfer',
         title: 'Transfer',
         action: () => handleWorkflowAction('Transfer', itemInventoryWarehouse),
         type: 'TransferTo',
      },
      {
         name: 'actionReturn',
         title: 'Return',
         action: () => handleWorkflowAction('Return', itemInventoryWarehouse),
         type: 'CheckIn',
      },
      {
         name: 'copy',
         title: 'Copy',
         action: () =>
            profile.AllowAddTool
               ? handleCopyPrompt()
               : setIsItemLimitPrompt(true),
         type: '',
      },
      {
         name: 'adjust',
         title: 'Adjust',
         action: () => handleAdjustClick(itemInventoryWarehouse),
         type: 'Adjust',
      },
      {
         name: 'remove',
         title: 'Delete',
         action: () => handleDeletePrompt(),
         type: 'DeleteTool',
      },
      {
         name: 'add-note',
         title: 'Note',
         action: () => handleAddNote(),
         type: 'AddNote',
      },
      {
         name: 'manage-kit',
         title: 'Manage Kit',
         action: () => manageKit(),
         type: 'AddtokitOpenItems',
      },
      {
         name: 'add-to-kit',
         title: 'Add to Kit',
         action: () => addToKit(itemInventoryWarehouse),
         type: 'AddtokitOpenKit',
      },
      {
         name: 'remove-from-kit',
         title: 'Remove from Kit',
         action: () => removeFromKit(),
         type: 'RemoveFromKit',
      },
      {
         name: 'makeUnavailable',
         title: 'Make Unavailable',
         action: () => handleWorkflowAction('Make Unavailable'),
         type: 'MakeUnavailable',
      },
      {
         name: 'makeAvailable',
         title: 'Make Available',
         action: () => handleWorkflowAction('Make Available'),
         type: 'MakeAvailable',
      },
   ]

   const {
      upload,
      changePosition,
      deleteImage,
      isPositionLoading,
      isPositionSuccess,
   } = useItemUpload()
   const { data: itemNotesData, itemNotesList, refetch } = useItemNotes()
   const { data: itemHistoryData, getItemHistoryData } = useItemHistory()

   const {
      data: listViewData,
      getItemView,
      isLoading: isItemLoading,
      isRefetching: isItemRefetching,
      isSuccess: isItemSuccess,
   } = useItemsView()

   const {
      setPath,
      error,
      remove,
      add,
      isRemoving,
      isSuccessRemove,
      isErrorRemove,
      isAdding,
      isSuccessAdd,
      isErrorAdd,
   } = useItems()

   const {
      result: getItemKitData,
      addKit,
      getKits,
      deleteKit,
      isSuccessKits,
      isErrorCreate: isErrorAddToKit,
      isSuccessCreate: isSuccessAddToKit,
      isErrorDeleteKit,
      isSuccessDeleteKit,
      isLoadingKits,
      isCreating: isAddingToKit,
      isDeletingKit,
   } = useItemGetKits()

   useEffect(() => {
      setPath(CRUD_TABLE.crudPaths, CRUD_TABLE.key)
   }, [])

   useEffect(() => {
      // if itemform isnt empty then set selected item
      if (Object.keys(itemForm).length !== 0) {
         setSelectedItemsForWorkflow([itemForm])
      }
   }, [itemForm])

   useEffect(() => {
      //handles setting the item modal form
      if (listViewData?.data?.Data && isItemSuccess) {
         let data = listViewData?.data?.Data
         showActionForm(
            itemAction ?? Actions.Edit,
            data?.Id,
            data?.Title,
            data?.ManufacturerId,
            data?.Category,
            data
         )
      }
   }, [listViewData, isItemSuccess])

   useEffect(() => {
      if (isSuccessKits && getItemKitData?.data) {
         setItemKitOptions(convertNoteTypesToOptions(getItemKitData.data))
         setIsAddToKitModalOpen(true)
      }
   }, [isSuccessKits, getItemKitData])

   useEffect(() => {
      if (itemToRemove && isItemModalVisible) {
         setIsItemModalVisible(false)
      }
   }, [itemToRemove, isItemModalVisible])

   useEffect(() => {
      handleSnackbar('Item has been deleted.', Severity.SUCCESS)
   }, [isSuccessRemove])

   useEffect(() => {
      handleSnackbar(error, Severity.ERROR)
   }, [isErrorRemove])

   useEffect(() => {
      const copiedItemName = `${itemForm.Title} (Copy)`
      handleSnackbar(
         `Item has been copied with the name "${copiedItemName}". Please visit the items page to edit it.`,
         Severity.SUCCESS
      )
   }, [isSuccessAdd])

   useEffect(() => {
      handleSnackbar(error, Severity.ERROR)
   }, [isErrorAdd])

   useEffect(() => {
      handleSnackbar('Item has been added to the kit.', Severity.SUCCESS)
   }, [isSuccessAddToKit])

   useEffect(() => {
      handleSnackbar(error, Severity.ERROR)
   }, [isErrorAddToKit])

   useEffect(() => {
      handleSnackbar('Item has been removed from the kit.', Severity.SUCCESS)
   }, [isSuccessDeleteKit])

   useEffect(() => {
      handleSnackbar(error, Severity.ERROR)
   }, [isErrorDeleteKit])

   const handleSnackbar = (message: string, severity: Severity) => {
      setSnackbar((prevState) => ({ ...prevState, message, severity }))
   }

   const handleDeletePrompt = () => {
      setIsDeletePrompt(true)
      handleItemModalClose()
   }

   const handleDelete = (id: number) => {
      setItemId(id)
      removeItemFromAudit(itemForm)
      handleActionForm(Actions.Delete)
      setIsDeletePrompt(false)
   }

   const deleteItem = () => remove({ id: itemId })

   const handleCopyPrompt = () => {
      setIsCopyPrompt(true)
      handleItemModalClose()
   }

   const handleCopy = () => {
      handleActionForm(Actions.Copy)
      setIsCopyPrompt(false)
   }

   const copyItem = () => {
      const newItem = {
         ...itemForm,
         Title: `${itemForm.Title} (Copy)`,
         ManufacturerId: itemForm.ManufacturerId,
         copyId: itemForm.Id,
         SerialNumber: '',
         Barcode: '',
         Id: null,
         ToolLoanId: 0,
         localUniqueId: null,
         LocationTrackingLabel: null,
         CustomRentalRate1: itemForm.CustomRentalRate1Value,
         CustomRentalRate2: itemForm.CustomRentalRate2Value,
         CustomRentalRate3: itemForm.CustomRentalRate3Value,
         CustomRentalRate4: itemForm.CustomRentalRate4Value,
         itemImages: itemImages
            ?.filter((image) => !image.IsMatchedImage)
            ?.map((image) => ({
               OriginalId: image.OriginalId,
               Position: image.Position,
            })),
         Warehouses:
            itemForm?.Warehouses?.length > 0
               ? itemForm.Warehouses.map((warehouse: ItemWarehouse) => ({
                    WarehouseLocationId: warehouse.WarehouseLocationId,
                    totalQuantity: warehouse.AvailableQuantity,
                    reorderLevelQuantity: warehouse.ReorderLevelQuantity,
                    IsHomeWarehouse: warehouse.IsHomeWarehouse,
                 }))
               : [],
      }
      add(newItem)
      setItemAction(Actions.Readonly)
   }

   const handleAddNote = () => {
      setTabStatus(TabStatus.NOTES)
      setIsNewItemNote(true)
   }

   const manageKit = () => {
      setIsManageKitModalOpen(true)
      setIsItemModalVisible(false)
      refreshItemInList(itemForm)
   }

   const addToKit = (
      warehouse?: InventoryWarehouse,
      disableWarehouseSelection?: boolean
   ) => {
      setSelectedWarehouseForCheckout(warehouse)

      getKits({
         defaultLocationID: warehouse?.id ?? itemForm.DefaultLocationID,
         status: itemForm.Status,
         assignedToUserID: itemForm.AssignedToUserID,
         requestedLocationId: itemForm.RequestedLocationId,
         locationID: warehouse?.id ?? itemForm.LocationID,
         skip: null,
         take: null,
         query: null,
      })
      setWarehouseSelectionDisabled(disableWarehouseSelection ?? false)
   }

   const handleAddToKit = (obj: any) => {
      handleItemModalClose()
      addKit(obj)
      const refreshItem = {
         ...itemForm,
         KitId: obj?.KitID,
      }
      refreshItemInList(refreshItem)
   }

   const removeFromKit = () => {
      handleItemModalClose()
      setIsRemoveFromKitModal(true)
   }

   const handleRemoveFromKit = (obj: any) => {
      deleteKit(obj)
      setIsRemoveFromKitModal(false)
      const refreshItem = {
         ...itemForm,
         KitId: obj?.KitID,
      }
      refreshItemInList(refreshItem)
   }

   const handleAdjustClick = (
      selectedWarehouse: InventoryWarehouse,
      disableWarehouseSelection?: boolean
   ) => {
      setWarehouseSelectionDisabled(disableWarehouseSelection ?? false)
      setSelectedWarehouseForCheckout(selectedWarehouse)
      setIsAdjustModalOpen(true)
   }

   const postItem = (item: {}, localUniqueId) => {
      const formData = new FormData()
      formData.append('toolId', localUniqueId)
      formData.append('position', item.Position)
      formData.append('file[0]', item['File[0]'])
      formData.append('file[1]', item['File[1]'])
      upload(formData)
   }

   const handleItemModalClose = () => {
      setIsItemModalVisible(false)
      setIsNewItemNote(false)
      setActiveValue({})
      setIsAddToKitModalOpen(false)
      setIsAdjustModalOpen(false)
      setIsRemoveFromKitModal(false)
      setItemAction(Actions.Readonly)
   }

   const getItem = (
      Id: number,
      ToolLoanId: number | null,
      KitId: number | null,
      toolWarehouseId: number | null,
      localUniqueId: number | null,
      AuditItemID: number | null,
      e: MouseEvent
   ) => {
      const target = e.target as HTMLElement
      if (target.className === 'Id' || target.closest('.Id')) {
         return null
      }
      getItemView({
         Id,
         ToolLoanId: ToolLoanId ?? null,
         KitId,
         toolWarehouseId,
      })
      setItemId(Id)
      setSelectedItemUniqueId(localUniqueId)
      setSelectedAuditItemId(AuditItemID)
   }

   const showActionForm = (
      action: number | ((prevState: number) => number),
      id?: number,
      title?: string,
      manufacturerId?: number,
      category?: string,
      itemListData = []
   ) => {
      const obj = itemListData
      setItemId(id)
      setItemAction(action)

      setActiveValue(obj)
      setItemForm(obj)
      setItemImages([])

      itemNotesList(String(id))
      getItemHistoryData(String(id))
      setIsItemModalVisible(true)
   }

   const handleActionForm = (action: number, id?: number, obj?: Item) => {
      switch (action) {
         case Actions.Copy:
            copyItem()
            break
         case Actions.Delete:
            deleteItem()
            break
         default:
            break
      }
   }

   const refetchNoteTableData = () => refetch()

   const isQuantityItemsSelected = (array: Item[]) =>
      array.some(
         (x) =>
            x.ToolType == ToolTypes['Consumable'] ||
            x.ToolType == ToolTypes['Quantity Item']
      )

   const handleIncrementDecrementQuantity = (item: Item, val: string) => {
      const itemToAdjust = selectedItemsForWorkflow.find(
         (y) => y.localUniqueId === item.localUniqueId
      )
      if (val === 'dec' && itemToAdjust.quantityForCart > 1) {
         itemToAdjust.quantityForCart = itemToAdjust.quantityForCart - 1
      } else if (
         val === 'inc' &&
         itemToAdjust.quantityForCart < itemToAdjust.StatusQuantity
      ) {
         itemToAdjust.quantityForCart = itemToAdjust.quantityForCart + 1
      } else {
         return
      }
      setSelectedItemsForWorkflow([...selectedItemsForWorkflow])
   }

   const handleCustomTotalChange = (e: any, item: Item) => {
      e.target.value = parseInt(e.target.value)
      const itemToAdjust = selectedItemsForWorkflow.find(
         (y) => y.localUniqueId === item.localUniqueId
      )
      const adjustedValue =
         e.target.value === '' ? e.target.value : Number(e.target.value)
      // @ts-ignore
      itemToAdjust.quantityForCart = adjustedValue
      setSelectedItemsForWorkflow([...selectedItemsForWorkflow])
   }

   const handleWarehouseChange = (selectedWarehouse: Option | null) => {
      if (isAddToKitModalOpen) {
         getKits({
            defaultLocationID: Number(selectedWarehouse?.value),
            status: itemForm.Status,
            assignedToUserID: itemForm.AssignedToUserID,
            requestedLocationId: itemForm.RequestedLocationId,
            locationID: Number(selectedWarehouse?.value),
            skip: null,
            take: null,
            query: null,
         })
      }
      const checkoutWarehouse = selectedWarehouse
         ? inventoryWarehouses?.find(
              (x) => x.id === Number(selectedWarehouse.value)
           )
         : null
      setSelectedWarehouseForCheckout(checkoutWarehouse)
      const updatedItems = selectedItemsForWorkflow.map((x) => ({
         ...x,
         quantityForCart: 1, // set initial quantity to 1
         toolWarehouseId: checkoutWarehouse
            ? checkoutWarehouse.toolWarehouseId
            : null,
      }))
      setSelectedItemsForWorkflow(updatedItems)
   }

   const handleTransferToWarehouseChange = (
      selectedWarehouse: Option | null
   ) => {
      const transferToInvWarehouse = selectedWarehouse
         ? convertOptionToInventoryWarehouse(selectedWarehouse)
         : null
      setSelectedTransferToWarehouse(transferToInvWarehouse)
      const updatedItems = selectedItemsForWorkflow.map((x) => ({
         ...x,
         NewLocation: selectedWarehouse?.label,
      }))
      setSelectedItemsForWorkflow(updatedItems)
   }

   const handleWorkflowModalsClose = useCallback(() => {
      setIsCheckoutModalOpen(false)
      setIsQuantityModalOpen(false)
      setSelectedTransferToWarehouse(null)
   }, [])

   useEffect(() => {
      if (!isCheckoutModalOpen) {
         setSelectedWarehouseForCheckout(null)
         setSelectedTransferToWarehouse(null)
      }
   }, [isCheckoutModalOpen])

   const handleCommenceWorkflow = () => {
      if (isQuantityModalOpen) {
         setIsQuantityModalOpen(false)
      }
      setIsCheckoutModalOpen(true)
   }

   const request = useAPI()

   const getItemFromAPI = async ({
      Id,
      ToolLoanId,
      KitId,
      toolWarehouseId,
   }: GetItemOptions) => {
      const { data } = await request.get(
         `${PATH.ITEMS.LIST_VIEW}?Id=${Id}&ToolLoanId=${ToolLoanId}&KitId=${KitId}&toolWarehouseId=${toolWarehouseId}`
      )
      return data?.Data
   }

   const refreshItemInList = async (rawItem: Item) => {
      setLoading(true)
      const itemToRefresh = {
         ...rawItem,
         localUniqueId: selectedItemUniqueId,
         AuditItemID: selectedAuditItemId,
      }

      const { Id, ToolLoanId, KitId, Warehouse } = itemToRefresh
      const workflowType = checkoutAction?.label
      setItemId(Id)
      // Quantity items
      if (itemToRefresh?.ToolType === 2) {
         removeItemFromAudit(itemToRefresh)
         // only add the original item if it wasn't field transferred
         if (workflowType !== View.Field_Transfer) {
            const originalItem = await getItemFromAPI({
               Id: Id,
               ToolLoanId: ToolLoanId,
               KitId: KitId,
               toolWarehouseId: Warehouse?.ToolWarehouseID,
            })
            const updatedOriginalItem = {
               ...originalItem,
               localUniqueId: selectedItemUniqueId,
               AuditItemID: null,
            }
            handleAddAuditItem(updatedOriginalItem)
         }
         // handle adding the new item created by the workflow request
         if (
            workflowType === View.Loan ||
            workflowType === View.Field_Transfer
         ) {
            const addedItem = await getItemFromAPI({
               Id: workflowItem?.ToolId,
               ToolLoanId: workflowItem?.ToolLoanId,
               KitId: workflowItem?.KitId,
               toolWarehouseId: workflowItem?.Warehouse?.ToolWarehouseID,
            })
            handleAddAuditItem(addedItem)
         }
         // handle refreshing available items for return request
         if (workflowType === View.Return) {
            const availableItem: any = pagedTableData.find(
               (item) => item?.Id === itemToRefresh?.Id && item?.Status === 0
            )
            if (availableItem) {
               removeItemFromAudit(availableItem)
               const updatedItem = await getItemFromAPI({
                  Id: availableItem?.Id,
                  ToolLoanId: availableItem?.ToolLoanId,
                  KitId: availableItem?.KitId,
                  toolWarehouseId: availableItem?.Warehouse?.ToolWarehouseID,
               })
               const updatedAvailableItem = {
                  ...updatedItem,
                  localUniqueId: selectedItemUniqueId,
                  AuditItemID: null,
               }
               handleAddAuditItem(updatedAvailableItem)
            }
         }
      }
      // None quantity items
      if (itemToRefresh?.ToolType !== 2) {
         removeItemFromAudit(itemToRefresh)
         if (workflowItem) {
            const addedWorkflowItem = await getItemFromAPI({
               Id: workflowItem?.ToolId ?? Id,
               ToolLoanId: workflowItem?.ToolLoanId ?? null,
               KitId: workflowItem?.KitId ?? null,
               toolWarehouseId: workflowItem?.Warehouse?.ToolWarehouseID,
            })
            handleAddAuditItem(addedWorkflowItem)
         } else {
            const updatedItem = await getItemFromAPI({
               Id: Id,
               ToolLoanId: ToolLoanId,
               KitId: KitId,
               toolWarehouseId: Warehouse?.ToolWarehouseID,
            })
            handleAddAuditItem(updatedItem)
         }
      }
      handleItemModalClose()
      setLoading(false)
   }

   const handleResetPageAfterCheckout = () => {
      setIsAdjustModalOpen(false)
      setIsCheckoutModalOpen(false)
      setIsQuantityModalOpen(false)
      refreshItemInList(itemForm)
   }

   const handleWorkflowAction = (
      workflowOption: string,
      warehouse?: InventoryWarehouse,
      disableSelection?: boolean
   ) => {
      let workflowPath
      if (workflowOption === 'Transfer') {
         if (selectedItemsForWorkflow[0].StatusDesc === 'Available') {
            workflowPath = checkoutOptions.find(
               (x) => x.label === 'Warehouse Transfer'
            )
         } else {
            workflowPath = checkoutOptions.find(
               (x) => x.label === 'Field Transfer'
            )
         }
      } else {
         workflowPath = checkoutOptions.find((x) => x.label === workflowOption)
      }
      setCheckoutAction(workflowPath)

      if (
         isQuantityItemsSelected(selectedItemsForWorkflow) &&
         workflowOption !== 'Make Available' &&
         workflowOption !== 'Make Unavailable'
      ) {
         let quantityForCart: number
         if (workflowOption === 'Warehouse Transfer') {
            quantityForCart = warehouse?.quantity ?? 1
         } else if (workflowOption?.includes('Transfer')) {
            quantityForCart = selectedItemsForWorkflow[0]?.StatusQuantity ?? 1
         } else {
            quantityForCart = 1
         }
         const quantItem = {
            ...selectedItemsForWorkflow[0],
            quantityForCart: quantityForCart,
         }
         setSelectedWarehouseForCheckout(warehouse ?? null)
         setSelectedItemsForWorkflow([quantItem])
         setIsQuantityModalOpen(true)
         setWarehouseSelectionDisabled(disableSelection ?? false)
      } else {
         handleCommenceWorkflow()
      }
   }

   const handleWorkflowSuccess = (response) => {
      if (response?.SuccessfulWithLoanID?.length > 0) {
         const loanItem =
            response?.SuccessfulWithLoanID[
               response?.SuccessfulWithLoanID.length - 1
            ]
         setWorkflowItem(loanItem)
      } else if (response?.SuccessfulWithKitID?.length > 0) {
         const kitItem =
            response?.SuccessfulWithKitID[
               response?.SuccessfulWithKitID.length - 1
            ]
         setWorkflowItem(kitItem)
      } else {
         setWorkflowItem(null)
      }
   }
   // End item modal functionality

   const isLoading =
      isItemLoading ||
      isItemRefetching ||
      isRemoving ||
      isAdding ||
      isAddingToKit ||
      isDeletingKit ||
      isLoadingKits ||
      auditIsLoading ||
      loading ||
      isPositionLoading

   return (
      <>
         {isLoading && <LoadingOverlay />}
         <SnackBar
            message={snackbar.message}
            open={
               snackbar.message !== '' && snackbar.severity !== Severity.INFO
            }
            severity={snackbar.severity}
            disableAutoClose={snackbar.disableAutoClose}
            resetMessageStateHandler={() => setSnackbar(closedSnackbarState)}
         />
         {!!selectedAuditFormData.AuditID &&
            selectedAuditFormData.str_Status !== 'Draft' && (
               <QuickFilterButtons
                  quickFilterOptions={[
                     {
                        id: String(ViewName.CONFIRMED),
                        label: 'Confirmed',
                        amount: confirmedTotalCount,
                     },
                     {
                        id: String(ViewName.UNCONFIRMED),
                        label: 'Unconfirmed',
                        amount: unconfirmedTotalCount,
                     },
                     {
                        id: String(ViewName.FOUND),
                        label: 'Found',
                        amount: foundTotalCount,
                     },
                  ]}
                  activeId={String(selectedView)}
                  onButtonClick={(stringifiedId) => handleTabClick(Number(stringifiedId))}
               />
            )}
         <div className={styles.itemList}>
            <div
               className={
                  selectedView === ViewName.UNCONFIRMED
                     ? ''
                     : styles.nonActiveTab
               }
            >
               {!unconfirmed?.length && !isLoading ? (
                  <p className={styles.noItemsText}>{noItemsText}</p>
               ) : (
                  <TableWrapper
                     tableType="Audit Items"
                     columns={columns}
                     data={pagedTableData as any}
                     getItems={handleGetItems}
                     totalCount={unconfirmedTotalCount}
                     extraClassName=""
                     isLoading={isLoading}
                     isPageReset={false}
                     searchCriteria=""
                     setItemId={() => {}}
                     setPageSized={() => null}
                     hideResultsTitle
                     listViewData={listViewData}
                     isListLoading={isItemLoading}
                     isListRefetching={isItemRefetching}
                     isListSuccess={isItemSuccess}
                     getItem={getItem}
                  />
               )}
            </div>

            <div
               className={
                  selectedView === ViewName.CONFIRMED ? '' : styles.nonActiveTab
               }
            >
               {!confirmed?.length && !isLoading ? (
                  <p className={styles.noItemsText}>{noItemsText}</p>
               ) : (
                  <TableWrapper
                     tableType="Audit Items"
                     columns={columns}
                     data={pagedTableData as any}
                     getItems={handleGetItems}
                     totalCount={confirmedTotalCount}
                     extraClassName=""
                     isLoading={isLoading}
                     isPageReset={false}
                     searchCriteria=""
                     setItemId={() => {}}
                     setPageSized={() => null}
                     hideResultsTitle
                     listViewData={listViewData}
                     isListLoading={isItemLoading}
                     isListRefetching={isItemRefetching}
                     isListSuccess={isItemSuccess}
                     getItem={getItem}
                  />
               )}
            </div>

            <div
               className={
                  selectedView === ViewName.FOUND ? '' : styles.nonActiveTab
               }
            >
               {!found?.length && !isLoading ? (
                  <p className={styles.noItemsText}>{noItemsText}</p>
               ) : (
                  <TableWrapper
                     tableType="Audit Items"
                     columns={columns}
                     data={pagedTableData as any}
                     getItems={handleGetItems}
                     totalCount={foundTotalCount}
                     extraClassName=""
                     isLoading={isLoading}
                     isPageReset={false}
                     searchCriteria=""
                     setItemId={() => {}}
                     setPageSized={() => null}
                     hideResultsTitle
                     listViewData={listViewData}
                     isListLoading={isItemLoading}
                     isListRefetching={isItemRefetching}
                     isListSuccess={isItemSuccess}
                     getItem={getItem}
                  />
               )}
            </div>

            {itemToRemove !== null && (
               <Modal
                  isModalVisible={itemToRemove !== null}
                  closeModal={handleCancelRemoveItem}
                  title="Warning"
               >
                  <div className={auditsStyles.deleteForm}>
                     <p>
                        Are you sure you want to remove{' '}
                        <strong>{itemToRemove.Title}</strong> from the Audit?
                     </p>

                     <Row gutterWidth={20}>
                        <Col xs={6}>
                           <Button
                              type="button"
                              minWidth="100%"
                              variant="danger"
                              onClick={handleConfirmRemoveItem}
                           >
                              OK
                           </Button>
                        </Col>
                        <Col xs={6}>
                           <Button
                              type="button"
                              minWidth="100%"
                              variant="tertiary"
                              onClick={handleCancelRemoveItem}
                              isReset
                           >
                              Cancel
                           </Button>
                        </Col>
                     </Row>
                  </div>
               </Modal>
            )}
         </div>
         {isItemModalVisible && (
            <Modal
               isModalVisible={isItemModalVisible}
               closeModal={handleItemModalClose}
               title={itemForm?.Title}
               itemButtons={allItemButtons}
               permissions={itemForm.Permissions ?? []}
               marginTop={isMobile && '2rem'}
               isItemModal
            >
               <CrudItems
                  isNewItemNote={isNewItemNote}
                  setIsNewItemNote={setIsNewItemNote}
                  postItem={postItem}
                  setItemImages={setItemImages}
                  itemImages={itemImages}
                  deleteImage={deleteImage}
                  changePosition={changePosition}
                  isPositionSuccess={isPositionSuccess}
                  setTabStatus={setTabStatus}
                  tabStatus={tabStatus}
                  activeValue={activeValue}
                  isLoading={isLoading}
                  itemTypes={itemTypeOptions}
                  categories={categoryOptions}
                  manufacturers={manufacturerOptions}
                  locations={locationOptions}
                  conditions={conditionOptions}
                  itemNotesData={itemNotesData}
                  itemHistoryData={itemHistoryData}
                  handleAction={handleActionForm}
                  closeModal={handleItemModalClose}
                  itemId={itemId}
                  itemAction={itemAction}
                  isModalVisible={isItemModalVisible}
                  itemForm={itemForm}
                  setItemForm={setItemForm}
                  resetRef={resetRef}
                  getItemView={getItemView}
                  refetchNoteTableData={refetchNoteTableData}
                  parentPageView={CrudItemsParentView.Audits}
                  handleWorkflowAction={handleWorkflowAction}
                  handleAdjustClick={handleAdjustClick}
                  handleAddToKitClick={addToKit}
                  isAdminCompanyUser
               />
            </Modal>
         )}
         {isCopyPrompt && (
            <WarningModal
               isModalOpen={isCopyPrompt}
               handleClose={() => setIsCopyPrompt(false)}
               title={'Copy Confirmation'}
               message={'Are you sure you want to copy this item?'}
               primaryButtonTitle={'Ok'}
               primaryButtonMethod={handleCopy}
               primaryButtonVariant={'primary'}
               secondaryButtonTitle={'Cancel'}
               secondaryButtonMethod={() => setIsCopyPrompt(false)}
               secondaryButtonVariant={'tertiary'}
            />
         )}
         {isItemLimitPrompt && (
            <WarningModal
               isModalOpen={isItemLimitPrompt}
               handleClose={() => setIsItemLimitPrompt(false)}
               title={'Item Limit Reached'}
               hasChildren
               message="Need more items?"
               zIndex="9999"
               primaryButtonTitle={'Close'}
               primaryButtonVariant={'tertiary'}
               primaryButtonMethod={() => setIsItemLimitPrompt(false)}
            >
               <Alert message="You have reached the item limit for your free account. Explore ShareMyToolbox paid system for more items and capabilities." />
            </WarningModal>
         )}
         {isDeletePrompt && (
            <WarningModal
               isModalOpen={isDeletePrompt}
               handleClose={() => setIsDeletePrompt(false)}
               title={'Delete Item'}
               message={'Are you sure you want to delete this item?'}
               primaryButtonTitle={'Delete'}
               primaryButtonMethod={(id) => handleDelete(id)}
               primaryButtonVariant={'danger'}
               secondaryButtonTitle={'Cancel'}
               secondaryButtonMethod={() => setIsDeletePrompt(false)}
               secondaryButtonVariant={'tertiary'}
               hasChildren={itemForm?.Warehouses?.length > 1}
            >
               <Alert
                  severity={Severity.WARNING}
                  message="All available quantities of this item will be
                     removed from all warehouses."
               />
            </WarningModal>
         )}
         {isManageKitModalOpen && (
            <ManageKitModal
               isModalOpen={isManageKitModalOpen}
               handleClose={() => setIsManageKitModalOpen(false)}
               kitId={itemId}
               itemForm={itemForm}
            />
         )}
         {isAddToKitModalOpen && (
            <AddToKitModal
               isModalOpen={isAddToKitModalOpen}
               handleClose={() => handleItemModalClose()}
               handleAddToKit={handleAddToKit}
               cancel={() => setIsAddToKitModalOpen(false)}
               kitOptions={itemKitOptions}
               addToKitQty={addToKitQty}
               setAddToKitQty={setAddToKitQty}
               itemForm={itemForm}
               selectedWarehouse={selectedWarehouseForCheckout}
               warehouseOptions={warehouseOptions}
               handleWarehouseChange={handleWarehouseChange}
               disabledWarehouseSelection={warehouseSelectionDisabled}
            />
         )}
         {isRemoveFromKitModal && (
            <RemoveFromKitModal
               isModalOpen={isRemoveFromKitModal}
               handleClose={() => setIsRemoveFromKitModal(false)}
               handleRemoveFromKit={handleRemoveFromKit}
               itemForm={itemForm}
            />
         )}

         {isQuantityModalOpen && (
            <ItemQuantityModal
               isModalOpen={isQuantityModalOpen}
               handleClose={handleWorkflowModalsClose}
               handleAdjustTotal={handleIncrementDecrementQuantity}
               handleCustomTotalChange={handleCustomTotalChange}
               startWorkflowAction={handleCommenceWorkflow}
               items={selectedItemsForWorkflow}
               checkoutAction={checkoutAction}
               warehouseOptions={warehouseOptions}
               selectedWarehouse={selectedWarehouseForCheckout}
               handleWarehouseChange={handleWarehouseChange}
               handleTransferToWarehouseChange={handleTransferToWarehouseChange}
               selectedTransferToWarehouse={selectedTransferToWarehouse}
               enableLocationTracking={settings?.EnableLocationTracking}
               warehouseSelectionDisabled={warehouseSelectionDisabled}
            />
         )}
         {isCheckoutModalOpen && (
            <CheckoutModal
               isModalOpen={isCheckoutModalOpen}
               handleClose={handleWorkflowModalsClose}
               checkoutAction={checkoutAction}
               selectedItems={selectedItemsForWorkflow}
               handlePageCancelAndReset={handleResetPageAfterCheckout}
               viewTitle
               handleWorkflowSuccess={handleWorkflowSuccess}
               isAdminCompanyUser
               selectedWarehouse={selectedWarehouseForCheckout}
               quantityInCart={
                  selectedItemsForWorkflow[0]?.quantityForCart ?? 1
               }
               selectedTransferToWarehouse={selectedTransferToWarehouse}
            />
         )}

         {isAdjustModalOpen && (
            <AdjustModal
               isOpen={isAdjustModalOpen}
               setIsOpen={setIsAdjustModalOpen}
               ItemId={itemId}
               enableLocationTracking={settings?.EnableLocationTracking}
               onAdjustComplete={handleItemModalClose}
               handleSnackbar={handleSnackbar}
               selectedWarehouse={selectedWarehouseForCheckout}
               handleWarehouseChange={handleWarehouseChange}
               itemWarehouses={itemForm?.Warehouses ?? []}
               warehouseOptions={warehouseOptions}
               disabledWarehouseSelection={warehouseSelectionDisabled}
               isQuantityItem={itemForm.ToolType == ToolTypes['Quantity Item']}
               locationOptions={locationOptions}
               connectionOptions={connectionOptions}
            />
         )}
         {auditNoteDetails && (
            <AuditNoteModal
               open={auditNoteModalOpen}
               handleClose={handleAuditNoteModalClose}
               noteDetails={auditNoteDetails}
            />
         )}
      </>
   )
}

export default memo(ItemAuditedList)
